import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { DropdownMenuGroups } from '@/features/analytics/invite-links/ui/InviteLinksTable/DropdownMenuGroups'
import {
    useDeleteInviteLinksGroupSitesMutation,
    useGetGroupsBySiteIdQuery,
} from '@/shared/api'
import { MagnifyingGlassSVGIcon } from '@/shared/assets'
import { SortHeader, Table } from '@/shared/components'
import { GreyRubleIconInline, RubleIconInline } from '@/shared/ui'
import { stopPropagation } from '@/true-fsd/shared/utils/react'

import { CpmCell } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/cells'
import { isMasterLink } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/masterLink'
import { useInviteLinksStore } from '@/features/analytics/model'
import {
    useActiveWorkspace,
    workspacePath,
} from '@/true-fsd/data/advertiser-account'
import { Checkbox2 } from '@/true-fsd/shared/ui/Checkbox2'
import { useGroupsSearch } from '../hooks/useGroupsSearch'
import {
    GroupsSiteFormattedRowType,
    useGroupsSitesFormattedRows,
} from '../hooks/useGroupsSitesFormattedRows'
import { useGroupsSortedData } from '../hooks/useGroupsSortedData'

export const InviteLinksGroupsSitesTable = ({
    className,
    siteId,
    handleCheckboxChange,
    isRowSelected,
}: {
    className?: string
    siteId: number
    handleCheckboxChange: (row: GroupsSiteFormattedRowType) => void
    isRowSelected: (row: GroupsSiteFormattedRowType) => boolean
}) => {
    const { selectedGroups, clearSelectedGroupsByChannel } =
        useInviteLinksStore()

    const deleteInviteLinksGroupSitesMutation =
        useDeleteInviteLinksGroupSitesMutation()

    const navigate = useNavigate()

    const { data: groups = [], isLoading } = useGetGroupsBySiteIdQuery(siteId)

    const formattedRows = useGroupsSitesFormattedRows(groups)
    const { searchQuery, setSearchQuery, filteredData } =
        useGroupsSearch(formattedRows)
    const { sortedData, sortConfig, handleSort } =
        useGroupsSortedData(filteredData)

    useEffect(() => {
        return () => {
            clearSelectedGroupsByChannel(siteId)
        }
    }, [siteId, clearSelectedGroupsByChannel])

    const tableHead = (
        <tr>
            <th style={{ verticalAlign: 'middle', cursor: 'pointer' }}>
                <div className="flex h-full items-center justify-center">
                    <DropdownMenuGroups
                        clearSelectedGroupsById={clearSelectedGroupsByChannel}
                        selectedGroups={selectedGroups}
                        deleteMutation={deleteInviteLinksGroupSitesMutation}
                        workspaceId={siteId}
                    />
                </div>
            </th>
            <th>
                <div className="search-header">
                    <div className="search-input-wrapper">
                        <MagnifyingGlassSVGIcon className="search-header__icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Название"
                            className="search-input"
                        />
                    </div>
                    <SortHeader<GroupsSiteFormattedRowType>
                        title=""
                        field="groupName"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </div>
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Кол-во ссылок"
                    field="linksCount"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title={
                        <>
                            Бюджет <GreyRubleIconInline />
                        </>
                    }
                    field="budget"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Дата создания"
                    field="creationDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Кол-во подписчиков"
                    field="subscribers"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="CPM"
                    field="cpm"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Охват"
                    field="reach"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Users"
                    field="users"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="CPU"
                    field="cpu"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="CTR%"
                    field="ctr_in_percent"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="Leads"
                    field="leads"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="CPL"
                    field="cpl"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsSiteFormattedRowType>
                    title="C%"
                    field="c_in_percent"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
        </tr>
    )

    const { activeWorkspace } = useActiveWorkspace()

    const tableBody = (
        <>
            {sortedData.map((row) => (
                <tr
                    key={row.id}
                    onClick={() =>
                        navigate(
                            workspacePath(
                                activeWorkspace,
                                `analytics/links/${row.id}`
                            )
                        )
                    }
                    className="group-name-link"
                >
                    <td>
                        <div className="flex h-full items-center justify-center">
                            <Checkbox2
                                checked={isRowSelected(row)}
                                onClick={stopPropagation}
                                onCheckedChange={() => {
                                    handleCheckboxChange(row)
                                }}
                                className="row-checkbox"
                            />
                        </div>
                    </td>
                    <td
                        className={
                            isMasterLink(row.groupName) ? '!font-semibold' : ''
                        }
                    >
                        {row.groupName}
                    </td>
                    <td>{row.linksCount}</td>
                    <td>
                        {row.budget} <RubleIconInline />
                    </td>
                    <td>
                        {new Date(row.creationDate).toLocaleDateString('ru-RU')}
                    </td>
                    <td>{row.subscribers}</td>
                    <td>
                        <CpmCell>{row.cpm}</CpmCell>
                    </td>
                    <td>{row.reach}</td>
                    <td>{row.users}</td>
                    <td>
                        {row.cpu.toFixed(0)} <RubleIconInline />
                    </td>
                    <td>{row.ctr_in_percent.toFixed(2)}%</td>
                    <td>{row.leads}</td>
                    <td>
                        {row.cpl.toFixed(0)} <RubleIconInline />
                    </td>
                    <td>{row.c_in_percent.toFixed(2)}%</td>
                </tr>
            ))}
        </>
    )

    return (
        <Table
            isLoading={isLoading}
            thead={tableHead}
            tbody={tableBody}
            baseTableClassName={className}
            className={'groups-table'}
        />
    )
}
